import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useState } from 'react';
import { MenuCrawler } from 'types/menuCrawler';

// 使っているクローラーをチェックする
const checkCrawler = (
  crawlers: number[],
  checkList: (MenuCrawler & { checked: boolean })[],
) => {
  crawlers.forEach((crawlerId) => {
    const crawler = checkList.find(
      (menuCrawler) => menuCrawler.crawlerId === crawlerId,
    );
    if (crawler && !crawler.checked) {
      crawler.checked = true;
      if (crawler.targetCrawlers) {
        checkCrawler(crawler.targetCrawlers, checkList);
      }
    }
  });
};

// 使っていないクローラーを削除する
export const deleteUnusedCrawlers = (
  menuCrawlers: MenuCrawler[],
): MenuCrawler[] => {
  const checkList = menuCrawlers.map((menuCrawler) => ({
    ...menuCrawler,
    targetCrawlers: menuCrawler.targetCrawlers?.concat() ?? null,
    checked: false,
  }));

  const rootCrawlers = checkList
    .filter((menuCrawler) => menuCrawler.firstCrawler)
    .map((menuCrawler) => menuCrawler.crawlerId);
  checkCrawler(rootCrawlers, checkList);

  return menuCrawlers.filter((menuCrawler) => {
    const crawler = checkList.find(
      (c) => c.crawlerId === menuCrawler.crawlerId,
    );

    return crawler ? crawler.checked : false;
  });
};

/**
 * メニューに紐づくクローラーを管理するユーティリティを提供します。
 * @returns
 */
export const useMenuCrawlers = () => {
  const [menuCrawlers, setMenuCrawlers] = useState<MenuCrawler[]>([]);

  const onAddCrawler = useCallback(
    (id: number, parentId: number) => {
      const nextMenuCrawlers = menuCrawlers.concat();

      // クローラーが存在しない場合は、クローラーを追加する
      if (!nextMenuCrawlers.some((crawler) => crawler.crawlerId === id)) {
        nextMenuCrawlers.push({
          menuId: -1,
          crawlerId: id,
          firstCrawler: parentId === -1,
          targetCrawlers: null,
          displayOrder: menuCrawlers.length + 1,
        });
      }

      if (parentId !== -1) {
        // 親クローラーの指定がある場合は、その子クローラーに追加する
        const parentCrawler = nextMenuCrawlers.find(
          (crawler) => crawler.crawlerId === parentId,
        );
        if (parentCrawler && !parentCrawler.targetCrawlers?.includes(id)) {
          if (parentCrawler.targetCrawlers === null) {
            parentCrawler.targetCrawlers = [];
          }
          parentCrawler.targetCrawlers?.push(id);
        }
      } else {
        // 親クローラーの指定がない場合は、ルートのクローラーに設定する
        const index = nextMenuCrawlers.findIndex(
          (crawler) => crawler.crawlerId === id,
        );
        if (index !== -1) {
          nextMenuCrawlers[index].firstCrawler = true;
        }
      }

      setMenuCrawlers(nextMenuCrawlers);
    },
    [menuCrawlers],
  );

  const onDeleteCrawler = useCallback(
    (id: number, parentId: number) => {
      let nextMenuCrawlers = menuCrawlers.concat();

      if (parentId !== -1) {
        // 親クローラーが存在する場合は、親クローラーの子クローラーから削除する
        const index = nextMenuCrawlers.findIndex(
          (crawler) => crawler.crawlerId === parentId,
        );
        if (index !== -1) {
          nextMenuCrawlers[index].targetCrawlers =
            nextMenuCrawlers[index].targetCrawlers?.filter(
              (crawlerId) => crawlerId !== id,
            ) ?? [];

          // 空配列でも問題はないが、既存のデータとの差分を減らすために null にする
          if (nextMenuCrawlers[index].targetCrawlers?.length === 0) {
            nextMenuCrawlers[index].targetCrawlers = null;
          }
        }
      } else {
        // ルートクローラーの場合は、ルートフラグをオフにする
        const target = nextMenuCrawlers.find(
          (crawler) => crawler.crawlerId === id,
        );
        if (target) {
          target.firstCrawler = false;
        }
      }

      // 使っていないクローラーを削除する
      nextMenuCrawlers = deleteUnusedCrawlers(nextMenuCrawlers);

      // クローラーの順番を振り直す
      nextMenuCrawlers = nextMenuCrawlers
        .concat()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((crawler, index) => ({
          ...crawler,
          displayOrder: index + 1,
        }));

      setMenuCrawlers(nextMenuCrawlers);
    },
    [menuCrawlers],
  );

  const onSortCrawler = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const crawlerId = Number(active.id);
      const targetId = Number(over.id);

      const oldIndex = menuCrawlers.findIndex(
        (crawler) => crawler.crawlerId === crawlerId,
      );
      const newIndex = menuCrawlers.findIndex(
        (crawler) => crawler.crawlerId === targetId,
      );
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const nextMenuCrawlers = arrayMove(menuCrawlers, oldIndex, newIndex).map(
        (crawler, index) => ({
          ...crawler,
          displayOrder: index + 1,
        }),
      );

      setMenuCrawlers(nextMenuCrawlers);
    },
    [menuCrawlers],
  );

  return {
    /** メニューに紐づくクローラー */
    menuCrawlers,
    /** メニューに紐づくクローラーを設定する */
    setMenuCrawlers,
    /** クローラーを追加する */
    onAddCrawler,
    /** クローラーを削除する */
    onDeleteCrawler,
    /** クローラーの順番を変更する */
    onSortCrawler,
  };
};
